import React from "react";
import Tooltip from "./Tooltip";

const DIMS = [
  { label: "Diversity of Experience", tip: "Sectors, geographies and functional breadth across C-suite careers. Scored from LinkedIn profiles and career histories." },
  { label: "Tenure Stability", tip: "Average years in current role across C-suite. Higher = institutional stability. Lower = transition risk." },
  { label: "External Hire Ratio", tip: "Percentage of C-suite hired externally versus promoted internally. Higher external ratio = fresh capability but lower institutional knowledge." },
  { label: "Digital & AI Capability", tip: "Percentage of leaders with technology or digital background, credentials or prior roles. Critical for transformation leadership." },
  { label: "Succession Depth", tip: "Visibility of #2 leaders in public profiles and job postings. Low score signals key person concentration risk." },
  { label: "Strategy Alignment", tip: "Degree to which leadership profile mix matches the stated business strategy and sector context. Scored from career histories vs strategic priorities." },
  { label: "Organisation Health", tip: "Composite of Glassdoor rating, CEO approval score, and employee review sentiment. Source: Glassdoor, Indeed, LinkedIn." },
];

const FULL_ARC = 179.1;
const DASH_OFFSET = -62.7;

function arcColor(score) {
  if (score >= 7) return "#C9A84C";
  if (score >= 5) return "#E8A020";
  return "#E84040";
}

function MiniGauge({ score, label, tip, size = 90 }) {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const fill = (score / 10) * FULL_ARC;
  const color = arcColor(score);
  const warn = score <= 5;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: size + 40 }}>
      <svg width={size} height={size} viewBox="0 0 90 90">
        {/* background arc */}
        <circle
          cx="45" cy="45" r={r} fill="none"
          stroke="#1e1e1e" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${FULL_ARC} ${circ}`}
          strokeDashoffset={DASH_OFFSET}
        />
        {/* fill arc */}
        <circle
          cx="45" cy="45" r={r} fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${fill} ${circ}`}
          strokeDashoffset={DASH_OFFSET}
          style={{ transition: "stroke-dasharray 0.5s ease" }}
        />
        {/* score */}
        <text x="45" y="43" textAnchor="middle" fill={color} fontSize="16" fontWeight="700" fontFamily="'Inter', sans-serif">
          {score}
        </text>
        <text x="45" y="56" textAnchor="middle" fill="#666" fontSize="9" fontFamily="'Overpass Mono', monospace">
          /10
        </text>
      </svg>
      <span style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: 9.5,
        color: "#888",
        textAlign: "center",
        lineHeight: 1.35,
        marginTop: 2,
        maxWidth: size + 30,
        minHeight: 26,
      }}>
        {warn && <span style={{ color: "#E8A020", marginRight: 3 }}>&#9888;</span>}
        {label} {tip && <Tooltip text={tip} width={240} />}
      </span>
    </div>
  );
}

export default function LeadershipGauges({ scores = [], companyName }) {
  const composite = scores.length
    ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
    : "0.0";

  const row1 = DIMS.slice(0, 4).map((d, i) => ({ label: d.label, tip: d.tip, score: scores[i] || 0 }));
  const row2 = DIMS.slice(4).map((d, i) => ({ label: d.label, tip: d.tip, score: scores[i + 4] || 0 }));

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      {/* Row 1: 4 gauges */}
      <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        {row1.map((g) => (
          <MiniGauge key={g.label} score={g.score} label={g.label} tip={g.tip} />
        ))}
      </div>
      {/* Row 2: 3 gauges centred */}
      <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        {row2.map((g) => (
          <MiniGauge key={g.label} score={g.score} label={g.label} tip={g.tip} />
        ))}
      </div>

      {/* Composite pill */}
      <div style={{
        marginTop: 20,
        padding: "8px 22px",
        background: "#111",
        border: "1px solid #C9A84C",
        borderRadius: 99,
        fontFamily: "'Overpass Mono', monospace",
        fontSize: 12,
        letterSpacing: "0.1em",
        color: "#C9A84C",
      }}>
        LEADERSHIP COMPOSITE &middot; {composite} / 10 <Tooltip text="Average of 7 leadership dimensions. Scored from public signals only — LinkedIn profiles, regulatory filings, job posting patterns, Glassdoor data and news analysis. Directional estimate." />
      </div>

      {/* Source line */}
      <p style={{
        fontFamily: "'Overpass Mono', monospace",
        fontSize: 10,
        color: "#666",
        textAlign: "center",
        marginTop: 10,
        letterSpacing: "0.04em",
      }}>
        Scored from public signals: LinkedIn profiles &middot; Glassdoor &middot; Regulatory filings &middot; Job posting patterns
      </p>
    </div>
  );
}
